import { useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import { Skeleton } from '@mui/material';
import type { Chat } from '@trinity/chat';
import {
  AttachmentsContext,
  DisplayModeContext,
} from '@trinity/chat-components/components/Chat/context';
import { useExportMessageTestingPpt } from '@trinity/chat-components/hooks/useExportMessageTestingPpt';
import { useMessageTestingRun } from '@trinity/chat-components/hooks/useMessageTestingRun';
import { MessageTestingFlatTable } from '../MessageTestingGrid/FlatTable';
import { MessageTestingPivotTable } from '../MessageTestingGrid/PivotTable';
import { MessageTestingResultsContext } from '../MessageTestingGrid/ResultsContainer';
import type { TestableMessage } from '../MessageTestingGrid/types';

interface MessageTestingGridProps {
  id: string;
}

export function MessageTestingGridAttachment({ id }: MessageTestingGridProps): React.JSX.Element | null {
  const attachments = useContext(AttachmentsContext);
  const attachment = useMemo(
    () => attachments.find((x) => x.id === id) as Chat.Attachment.MessageTestingGrid | undefined,
    [attachments, id]
  );

  if (!attachment) return null;

  return <MessageTestingGridInner attachment={attachment} />;
}

function MessageTestingGridInner({
  attachment,
}: {
  attachment: Chat.Attachment.MessageTestingGrid;
}): React.JSX.Element {
  const { runIdentifier, messages: rawMessages, segments: rawSegments, brand } = attachment.data;
  const displayMode = useContext(DisplayModeContext);
  const mtResultsCtx = useContext(MessageTestingResultsContext);

  const { status, progress, cells, cellErrors, error, rollups } = useMessageTestingRun(runIdentifier);
  const { exporting, exportPpt } = useExportMessageTestingPpt(runIdentifier);

  const messages: TestableMessage[] = useMemo(
    () =>
      rawMessages.map((m) => ({
        id: m.id,
        displayTitle: m.value.length > 60 ? m.value.slice(0, 57) + '...' : m.value,
        body: m.value,
        category: m.category,
        brand: brand ?? '',
        code: m.code,
      })),
    [rawMessages, brand]
  );

  const segments = useMemo(
    () => rawSegments.map((s) => ({ identifier: s.identifier, name: s.name })),
    [rawSegments]
  );

  return (
    <Wrapper>
      <GridCard>
        <GridHeader>
          <ExportButton onClick={exportPpt} disabled={status !== 'completed' || exporting}>
            {exporting ? 'Exporting…' : 'Download PPT'}
          </ExportButton>
        </GridHeader>

        {status === 'failed' ? (
          <ErrorBanner>
            <ErrorIcon>⚠</ErrorIcon>
            <ErrorContent>
              <ErrorTitle>Message testing failed</ErrorTitle>
              {error && <ErrorDetail>{error}</ErrorDetail>}
            </ErrorContent>
          </ErrorBanner>
        ) : (
          (cells.length > 0 || messages.length > 0) &&
          (displayMode === 'compact' ? (
            <>
              {mtResultsCtx && !mtResultsCtx.activeTestRunId && (
                <CompactHint>
                  <CompactHintLink onClick={() => mtResultsCtx.openResultsPanel(runIdentifier)}>
                    Open Message Testing panel
                  </CompactHintLink>{' '}
                  to view full results
                </CompactHint>
              )}
              <MessageTestingFlatTable
                messages={messages}
                segments={segments}
                cells={cells}
                cellErrors={cellErrors}
              />
            </>
          ) : (
            <MessageTestingPivotTable
              messages={messages}
              segments={segments}
              cells={cells}
              cellErrors={cellErrors}
            />
          ))
        )}
      </GridCard>

      {status !== 'failed' && (
        <SummaryBubble>
          <SummaryLabel>Executive Summary</SummaryLabel>
          {rollups?.matrixRollup.executiveSummary ? (
            <SummaryText>{rollups.matrixRollup.executiveSummary}</SummaryText>
          ) : (
            <SkeletonGroup>
              <Skeleton variant="text" width="100%" height={20} />
              <Skeleton variant="text" width="100%" height={20} />
              <Skeleton variant="text" width="85%" height={20} />
            </SkeletonGroup>
          )}
        </SummaryBubble>
      )}
    </Wrapper>
  );
}

// ─── Styled ─────────────────────────────────────────────────────────────────

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const GridCard = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  overflow: hidden;
  max-height: 500px;
`;

const CompactHint = styled.div`
  padding: 8px 16px;
  font-size: 13px;
  color: #555;
  background: #f0f4ff;
  border-bottom: 1px solid #e0e0e0;
`;

const CompactHintLink = styled.span`
  color: #6366f1;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: transparent;
  transition: text-decoration-color 0.15s;

  &:hover {
    text-decoration-color: currentColor;
  }
`;

const GridHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 8px 16px;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  color: white;
  background: #6366f1;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #4f46e5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SummaryBubble = styled.div`
  // padding: 10px;
  background: white;
  // border: 2px solid #e0e0e0;
  border-radius: 6px;
  margin-top: 8px;
`;

const SummaryLabel = styled.span`
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--black-l);
  letter-spacing: 0.5px;
`;

const SummaryText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
  white-space: pre-wrap;
  overflow-wrap: break-word;
`;

const SkeletonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  background: #fef2f2;
  border-top: 1px solid #fecaca;
`;

const ErrorIcon = styled.span`
  font-size: 18px;
  line-height: 1;
  flex-shrink: 0;
`;

const ErrorContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const ErrorTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #991b1b;
`;

const ErrorDetail = styled.span`
  font-size: 13px;
  line-height: 1.4;
  color: #b91c1c;
  white-space: pre-wrap;
  word-break: break-word;
`;

MessageTestingGridAttachment.displayName = 'Chat.Attachments.MessageTestingGrid';
